import * as THREE from 'three';
import { el } from './dom';
import {
  layoutPointers,
  type PointerDetail,
  type PointerPlacement,
  type PointerTarget,
  type ProjectedTarget,
} from './PointerLayout';

/**
 * The pointer overlay: callout headers floating over the scene, one per thing in it.
 *
 * ## Why DOM and not sprites
 *
 * The tag tool draws its labels as canvas-textured sprites so they are occluded and scaled by the
 * scene the same way the thing they name is — correct, for a marker that belongs *to* a plant. A
 * callout drawing is the opposite: its labels belong to the *reader*, not to the model. They have to
 * stay crisp at any distance, they have to be laid out against each other so four hundred of them
 * are still readable, and they must never be hidden by the shed they are describing. That is DOM
 * work, and doing it in the scene would mean re-implementing text layout and screen-space collision
 * inside WebGL for no gain.
 *
 * Two consequences follow for free. The overlay lives in the HUD tree, so photo mode clears it along
 * with everything else; and captures render the THREE scene through an off-screen target rather than
 * reading back the canvas (see `Renderer.renderToPixels`), so no pointer can ever leak into a render,
 * a photo, or an AI enhancement pass.
 *
 * ## Cost
 *
 * Elements are pooled by id and only repositioned per frame; their contents are rebuilt only when
 * what they say changes. A tick with a parked camera and an unchanged document early-outs on a
 * matrix comparison, which is the common case while someone reads the labels they just switched on.
 */

export interface ScenePointersDeps {
  /** Where the layer mounts. Its box is the coordinate frame every placement is expressed in. */
  host: HTMLElement;
  /** The 3D canvas, for the rect that NDC is mapped through. Null before one exists. */
  canvas: () => HTMLElement | null;
  camera: () => THREE.Camera;
  /** Everything worth pointing at, rebuilt when the document changes. */
  targets: () => readonly PointerTarget[];
  detail: () => PointerDetail;
  isSelected: (id: string) => boolean;
  /** Whether the manifest is hovering this id, flared out here — the other half of the linkage. */
  isHot: (id: string) => boolean;
  onSelect: (id: string, additive: boolean) => void;
  onFocus: (id: string) => void;
  /** Report which callout the pointer is over, so the manifest can flare the matching row. */
  onHover: (id: string | null) => void;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

export class ScenePointers {
  readonly root: HTMLElement;

  private readonly leaders: SVGSVGElement;
  private readonly labels: HTMLElement;

  private visible = false;
  /** Set whenever the document, the detail level, or the selection changes. */
  private dirty = true;

  /** Pooled per target id, so a callout is moved rather than rebuilt on every frame. */
  private readonly headers = new Map<string, HTMLElement>();
  private readonly lines = new Map<string, SVGLineElement>();
  private readonly marks = new Map<string, SVGCircleElement>();
  /** What each header currently says, so its DOM is only touched when the words change. */
  private readonly rendered = new Map<string, string>();

  /** Last camera and viewport the layout was solved for — the early-out. */
  private lastSignature = '';

  private readonly scratch = new THREE.Vector3();
  private readonly cameraPosition = new THREE.Vector3();

  constructor(private readonly deps: ScenePointersDeps) {
    this.leaders = document.createElementNS(SVG_NS, 'svg');
    this.leaders.setAttribute('class', 'lg-pointers__leaders');
    this.labels = el('div', { class: 'lg-pointers__labels' });
    this.root = el('div', { class: 'lg-pointers', attrs: { hidden: true } }, this.leaders, this.labels);
    deps.host.appendChild(this.root);
  }

  get isVisible(): boolean {
    return this.visible;
  }

  setVisible(visible: boolean): void {
    if (visible === this.visible) return;
    this.visible = visible;
    this.root.hidden = !visible;
    // A layer that was hidden while plants were added has a stale layout; make the first visible
    // frame the truth rather than one tick of the last camera it saw.
    this.dirty = true;
    if (!visible) this.clearPool();
  }

  /** Note that the targets, the detail level, or the selection changed. */
  invalidate(): void {
    this.dirty = true;
  }

  /**
   * Re-solve and redraw. Called once per frame from the app's render loop.
   *
   * Costs a matrix comparison and nothing else while the camera is still and the document is
   * unchanged, which is what someone reading a set of labels is doing most of the time.
   */
  tick(): void {
    if (!this.visible) return;
    const canvas = this.deps.canvas();
    if (!canvas) return;

    const canvasRect = canvas.getBoundingClientRect();
    const hostRect = this.root.getBoundingClientRect();
    if (hostRect.width < 1 || hostRect.height < 1) return;

    const camera = this.deps.camera();
    camera.updateMatrixWorld();
    const signature = `${camera.matrixWorldInverse.elements.join(',')}|${hostRect.width}x${hostRect.height}`;
    if (!this.dirty && signature === this.lastSignature) return;
    this.dirty = false;
    this.lastSignature = signature;

    this.leaders.setAttribute('viewBox', `0 0 ${hostRect.width} ${hostRect.height}`);
    this.leaders.setAttribute('width', String(hostRect.width));
    this.leaders.setAttribute('height', String(hostRect.height));

    const placements = layoutPointers(this.project(canvasRect, hostRect, camera), {
      detail: this.deps.detail(),
      viewport: { width: hostRect.width, height: hostRect.height },
    });
    this.draw(placements);
  }

  /* --------------------------------- Projection --------------------------------- */

  /**
   * World anchors to host-relative pixels.
   *
   * NDC lands in the *canvas* box and the callouts live in the *centre column* box, which is inset by
   * whatever the trays are currently occupying — so the difference is subtracted here rather than
   * assumed away. Anchors outside the column are kept: a plant tucked behind the palette still has a
   * label that can hang legitimately into the view, and the layout culls with its own margin.
   */
  private project(canvasRect: DOMRect, hostRect: DOMRect, camera: THREE.Camera): ProjectedTarget[] {
    camera.getWorldPosition(this.cameraPosition);
    const projected: ProjectedTarget[] = [];
    for (const target of this.deps.targets()) {
      this.scratch.set(target.world.x, target.world.y, target.world.z);
      const distance = this.scratch.distanceTo(this.cameraPosition);
      this.scratch.project(camera);
      if (this.scratch.z > 1) continue; // behind the camera
      projected.push({
        target,
        anchorX: ((this.scratch.x + 1) / 2) * canvasRect.width + canvasRect.left - hostRect.left,
        anchorY: ((-this.scratch.y + 1) / 2) * canvasRect.height + canvasRect.top - hostRect.top,
        distance,
      });
    }
    return projected;
  }

  /* ----------------------------------- Drawing ----------------------------------- */

  private draw(placements: readonly PointerPlacement[]): void {
    const detail = this.deps.detail();
    const seen = new Set<string>();

    for (const placement of placements) {
      const { target } = placement;
      seen.add(target.id);
      const header = this.header(target.id);
      const signature = `${detail}|${placement.dot ? 'dot' : 'full'}|${target.tag}|${target.title}|${target.detail}|${target.color}`;
      if (this.rendered.get(target.id) !== signature) {
        this.rendered.set(target.id, signature);
        this.fill(header, target, detail, placement.dot);
      }

      header.style.transform = `translate(${Math.round(placement.x)}px, ${Math.round(placement.y)}px)`;
      header.style.opacity = placement.opacity.toFixed(2);
      header.classList.toggle('is-selected', this.deps.isSelected(target.id));
      header.classList.toggle('is-hot', this.deps.isHot(target.id));

      this.leader(target.id, placement);
    }

    this.prune(seen);
  }

  private header(id: string): HTMLElement {
    const existing = this.headers.get(id);
    if (existing) return existing;
    const header = el('button', {
      class: 'lg-pointer',
      attrs: { type: 'button' },
      dataset: { id },
      on: {
        click: (event) => {
          event.stopPropagation();
          this.deps.onSelect(id, (event as MouseEvent).shiftKey);
        },
        dblclick: (event) => {
          event.stopPropagation();
          this.deps.onFocus(id);
        },
        pointerenter: () => this.deps.onHover(id),
        pointerleave: () => this.deps.onHover(null),
      },
    });
    this.headers.set(id, header);
    this.labels.appendChild(header);
    return header;
  }

  /**
   * Write a callout's contents.
   *
   * A demoted callout keeps its number and its colour and loses everything else: in a bed of eighty
   * hostas the number is what makes the planting countable, and it is also the one field that still
   * resolves to a row in the manifest.
   */
  private fill(
    header: HTMLElement,
    target: PointerTarget,
    detail: PointerDetail,
    dot: boolean,
  ): void {
    header.className = `lg-pointer${dot ? ' lg-pointer--dot' : ''}`;
    header.style.setProperty('--pointer-color', target.color);
    header.title = dot
      ? `${target.tag} · ${target.title} — ${target.detail}`
      : `${target.title} — ${target.detail}\nClick to select · double-click to fly there`;
    header.replaceChildren(
      el('span', { class: 'lg-pointer__chip', text: target.tag }),
      ...(dot || detail === 'tag'
        ? []
        : [
            el(
              'span',
              { class: 'lg-pointer__text' },
              el('span', { class: 'lg-pointer__title', text: target.title }),
              ...(detail === 'full'
                ? [el('span', { class: 'lg-pointer__detail', text: target.detail })]
                : []),
            ),
          ]),
    );
  }

  /**
   * The leader: a line from the thing to the underside of its header, and a mark on the thing itself.
   *
   * The mark matters more than the line. A label with a line running off it says "something over
   * there"; a label with a line ending in a ring on a trunk says *that one*, which is the whole
   * question the overlay exists to answer.
   */
  private leader(id: string, placement: PointerPlacement): void {
    let line = this.lines.get(id);
    if (!line) {
      line = document.createElementNS(SVG_NS, 'line');
      line.setAttribute('class', 'lg-pointers__leader');
      this.lines.set(id, line);
      this.leaders.appendChild(line);
    }
    let mark = this.marks.get(id);
    if (!mark) {
      mark = document.createElementNS(SVG_NS, 'circle');
      mark.setAttribute('class', 'lg-pointers__mark');
      mark.setAttribute('r', '2.5');
      this.marks.set(id, mark);
      this.leaders.appendChild(mark);
    }

    // The line meets the box on its underside, tracking the anchor sideways so a header that had to
    // step around a neighbour still points back at its own plant rather than at the gap it left.
    const footX = clamp(placement.anchorX, placement.x + 9, placement.x + placement.width - 9);
    line.setAttribute('x1', placement.anchorX.toFixed(1));
    line.setAttribute('y1', placement.anchorY.toFixed(1));
    line.setAttribute('x2', footX.toFixed(1));
    line.setAttribute('y2', (placement.y + placement.height).toFixed(1));
    line.setAttribute('stroke', placement.target.color);
    line.setAttribute('opacity', (placement.opacity * 0.7).toFixed(2));

    mark.setAttribute('cx', placement.anchorX.toFixed(1));
    mark.setAttribute('cy', placement.anchorY.toFixed(1));
    mark.setAttribute('fill', placement.target.color);
    mark.setAttribute('opacity', placement.opacity.toFixed(2));
  }

  /** Drop the elements for anything that fell out of the layout this frame. */
  private prune(seen: ReadonlySet<string>): void {
    for (const [id, header] of this.headers) {
      if (seen.has(id)) continue;
      header.remove();
      this.headers.delete(id);
      this.rendered.delete(id);
    }
    for (const [id, line] of this.lines) {
      if (seen.has(id)) continue;
      line.remove();
      this.lines.delete(id);
    }
    for (const [id, mark] of this.marks) {
      if (seen.has(id)) continue;
      mark.remove();
      this.marks.delete(id);
    }
  }

  private clearPool(): void {
    this.prune(new Set());
    this.lastSignature = '';
  }

  dispose(): void {
    this.clearPool();
    this.root.remove();
  }
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(low, value), Math.max(low, high));
}
